/**
 * Trunnion AI - Live Execution Feed
 * Real-time streaming event list with polling.
 */

import { useMemo } from "react";
import { Radio } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { useActiveRuns } from "@/hooks/queries/useDashboard";
import { EXECUTION_STATUS_CONFIG } from "@/config/constants";
import type { Execution } from "@/types";

function statusToBadgeVariant(
  status: string,
): "info" | "success" | "error" | "warning" | "purple" | "default" {
  switch (status) {
    case "running":
      return "info";
    case "completed":
      return "success";
    case "failed":
    case "timed_out":
      return "error";
    case "pending":
    case "paused":
      return "warning";
    case "waiting_approval":
      return "purple";
    default:
      return "default";
  }
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export function LiveExecutionFeed() {
  const { data } = useActiveRuns();

  const events: Execution[] = useMemo(() => {
    if (!data?.runs) return [];
    return data.runs.slice(0, 50);
  }, [data]);

  return (
    <Card padding="none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            <Radio className="h-4 w-4 inline mr-1.5 text-trunnion-gold-500" />
            Live Feed
          </CardTitle>
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400" />
            </span>
            <span className="text-xs text-trunnion-slate-400">Live</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0!">
        {events.length === 0 ? (
          <div className="flex items-center justify-center py-8 px-4">
            <p className="text-sm text-trunnion-slate-400">
              No active executions
            </p>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            <div className="divide-y divide-trunnion-slate-700/50">
              {events.map((run, index) => {
                const statusConfig = EXECUTION_STATUS_CONFIG[run.status];
                return (
                  <div
                    key={run.id}
                    className={`flex items-center gap-3 px-4 py-2.5 text-xs hover:bg-trunnion-slate-800/50 transition-colors ${
                      index === 0 ? "animate-slide-in" : ""
                    }`}
                  >
                    <span className="font-mono text-trunnion-slate-500 shrink-0">
                      {formatTime(run.startedAt)}
                    </span>
                    <span className="text-trunnion-slate-200 truncate flex-1 min-w-0">
                      {run.workflowName}
                    </span>
                    <Badge variant={statusToBadgeVariant(run.status)} dot>
                      {statusConfig?.label ?? run.status}
                    </Badge>
                    <span className="text-trunnion-slate-500 shrink-0 truncate max-w-25">
                      {run.triggeredBy}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
